/**
 * DataTable v2 pure model (PRD §4.10, T-36): sort cycling, row selection with
 * Shift-ranges, the header checkbox state, and column placement in the table
 * and in the card list. No React, no DOM, so it is unit-tested
 * (`src/utils/__tests__/tableModel.test.ts`).
 */

export type SortDir = 'asc' | 'desc';

export interface SortState {
  key: string;
  dir: SortDir;
}

/**
 * Next sort after a click on a column header:
 * another column → that column in its default direction; the same column →
 * the other direction; a third click → back to the list's own order (null).
 */
export function nextSort(current: SortState | null | undefined, key: string, firstDir: SortDir = 'asc'): SortState | null {
  if (!current || current.key !== key) return { key, dir: firstDir };
  if (current.dir === firstDir) return { key, dir: firstDir === 'asc' ? 'desc' : 'asc' };
  return null;
}

/** `aria-sort` for a sortable `<th>`. */
export function ariaSortFor(sort: SortState | null | undefined, key: string): 'ascending' | 'descending' | 'none' {
  if (!sort || sort.key !== key) return 'none';
  return sort.dir === 'asc' ? 'ascending' : 'descending';
}

/* ------------------------------------------------------------------ */
/* Selection                                                           */
/* ------------------------------------------------------------------ */

export type HeaderCheckState = 'none' | 'some' | 'all';

/** Toggle one id. Returns a new set. */
export function toggleId(selected: ReadonlySet<string>, id: string, checked?: boolean): Set<string> {
  const next = new Set(selected);
  const on = checked ?? !next.has(id);
  if (on) next.add(id);
  else next.delete(id);
  return next;
}

/**
 * Shift-click: every row between the anchor (the last row clicked without
 * Shift) and the target takes the target's new state. Without a usable
 * anchor it behaves like a plain toggle.
 */
export function selectRange(
  selected: ReadonlySet<string>,
  orderedIds: readonly string[],
  anchorId: string | null | undefined,
  targetId: string,
  checked: boolean,
): Set<string> {
  const to = orderedIds.indexOf(targetId);
  const from = anchorId ? orderedIds.indexOf(anchorId) : -1;
  if (to < 0) return new Set(selected);
  if (from < 0) return toggleId(selected, targetId, checked);
  const [a, b] = from <= to ? [from, to] : [to, from];
  const next = new Set(selected);
  for (let i = a; i <= b; i += 1) {
    if (checked) next.add(orderedIds[i]);
    else next.delete(orderedIds[i]);
  }
  return next;
}

/** State of the "select page" checkbox in the header. */
export function headerCheckState(selected: ReadonlySet<string>, pageIds: readonly string[]): HeaderCheckState {
  if (pageIds.length === 0) return 'none';
  let n = 0;
  for (const id of pageIds) if (selected.has(id)) n += 1;
  if (n === 0) return 'none';
  return n === pageIds.length ? 'all' : 'some';
}

/** Header checkbox click: all on the page selected → clear the page; otherwise select the whole page. */
export function togglePage(selected: ReadonlySet<string>, pageIds: readonly string[]): Set<string> {
  const next = new Set(selected);
  if (headerCheckState(selected, pageIds) === 'all') pageIds.forEach((id) => next.delete(id));
  else pageIds.forEach((id) => next.add(id));
  return next;
}

/** Drop ids that are no longer on screen (data refetched, page changed). Same set when nothing changes. */
export function pruneSelection(selected: ReadonlySet<string>, availableIds: readonly string[]): ReadonlySet<string> {
  if (selected.size === 0) return selected;
  const available = new Set(availableIds);
  let changed = false;
  const next = new Set<string>();
  selected.forEach((id) => {
    if (available.has(id)) next.add(id);
    else changed = true;
  });
  return changed ? next : selected;
}

/* ------------------------------------------------------------------ */
/* Column placement                                                    */
/* ------------------------------------------------------------------ */

/** 4 (C-25, D-15): a low-value column, shown from 2xl only, even when the table keeps every column. */
export type ColumnPriority = 1 | 2 | 3 | 4;

/**
 * Table visibility by priority: 1 always, 2 from lg, 3 from xl. (Below md a
 * `mobile="cards"` table is a card list, which shows every column.) A
 * `mobile="scroll"` table keeps every column at every width (`keepAll`): it
 * scrolls sideways instead of hiding data.
 */
export function priorityClass(priority: ColumnPriority | undefined, keepAll = false): string {
  // C-25 (clarity, D-15): the eight columns that matter fit at 1440; the rest wait for 2xl.
  if (priority === 4) return 'hidden 2xl:table-cell';
  if (keepAll) return '';
  if (priority === 2) return 'hidden lg:table-cell';
  if (priority === 3) return 'hidden xl:table-cell';
  return '';
}

export type CardSlot = 'title' | 'end' | 'badges' | 'meta' | 'details' | 'none';

/** Where a column goes in the card list when it does not say (`card`). */
export function defaultCardSlot(col: { key: string; card?: CardSlot; priority?: ColumnPriority; header?: string }, linkKey: string | null): CardSlot {
  if (col.card) return col.card;
  if (linkKey && col.key === linkKey) return 'title';
  if (!col.header) return 'none';
  if (col.priority === 3 || col.priority === 4) return 'details';
  return 'meta';
}

/** Rows a skeleton shows: the page size when known, capped so the page does not jump. */
export function skeletonRowCount(pageSize: number | undefined, fallback = 6): number {
  if (!pageSize || pageSize < 1) return fallback;
  return Math.min(pageSize, 10);
}

/* ------------------------------------------------------------------ */
/* Selectable rows, expansion, row actions                             */
/* ------------------------------------------------------------------ */

/**
 * Ids of the rows that can be selected (`isRowSelectable`), in order. The
 * header box, Shift-ranges and pruning work on these only; pinned rows are
 * never part of `data`, so they are never selectable.
 */
export function selectableIds<T>(items: readonly T[], ids: readonly string[], isSelectable?: (item: T) => boolean): string[] {
  if (!isSelectable) return [...ids];
  const out: string[] = [];
  items.forEach((item, i) => { if (isSelectable(item)) out.push(ids[i]); });
  return out;
}

/** Accessible name of a row action button: "‹action›: ‹row›" (never the "פעולות:" prefix of the ⋯). */
export function rowActionLabel(actionLabel: string, rowName: string): string {
  const a = actionLabel.trim();
  const r = rowName.trim();
  if (!r) return a;
  if (!a) return r;
  return `${a}: ${r}`;
}

export interface RowActionFlags {
  inline?: boolean;
  touchOnly?: boolean;
  touchVisible?: boolean;
  icon?: unknown;
}

/**
 * Where each row action goes. Pointer: `inline` actions with an icon are
 * icon buttons (shown on row hover / focus), the rest is the "⋯" menu,
 * `touchOnly` ones are dropped. Touch: `touchVisible` actions with an icon
 * stay visible as 44 px buttons next to the "⋯"; everything else is in the
 * "⋯" sheet.
 */
export function splitRowActions<A extends RowActionFlags>(actions: readonly A[], touch: boolean): { buttons: A[]; menu: A[] } {
  if (touch) {
    const buttons = actions.filter((a) => a.touchVisible && !!a.icon);
    return { buttons, menu: actions.filter((a) => !buttons.includes(a)) };
  }
  const visible = actions.filter((a) => !a.touchOnly);
  const buttons = visible.filter((a) => a.inline && !!a.icon);
  return { buttons, menu: visible.filter((a) => !buttons.includes(a)) };
}

/* ------------------------------------------------------------------ */
/* Horizontal scroll (mobile="scroll")                                 */
/* ------------------------------------------------------------------ */

export interface ScrollEdges {
  /** The box scrolls sideways at all. */
  overflow: boolean;
  /** Content is hidden past the inline-start edge (scrolled away from the start). */
  start: boolean;
  /** Content is hidden past the inline-end edge (more to see). */
  end: boolean;
}

/**
 * Which edges of a sideways-scrolling box hide content. Works for LTR
 * (`scrollLeft` 0 … max) and RTL (0 … −max in every current engine): the
 * distance from the start is `|scrollLeft|`. A 1 px tolerance absorbs
 * fractional widths.
 */
export function scrollEdges(scrollLeft: number, scrollWidth: number, clientWidth: number): ScrollEdges {
  const max = scrollWidth - clientWidth;
  if (!(max > 1)) return { overflow: false, start: false, end: false };
  const pos = Math.min(Math.abs(scrollLeft), max);
  return { overflow: true, start: pos > 1, end: pos < max - 1 };
}

/* ------------------------------------------------------------------ */
/* Footer (totals)                                                     */
/* ------------------------------------------------------------------ */

export interface FooterSpec<N> {
  cells: Partial<Record<string, N>>;
  label?: N;
}

/**
 * The footer cell of a column: its own cell, or, for the first column when it
 * has none, the footer label ("סה״כ (12)").
 */
export function footerCellFor<N>(footer: FooterSpec<N>, columnKey: string, isFirst: boolean): N | undefined {
  const own = footer.cells[columnKey];
  if (own !== undefined && own !== null) return own;
  return isFirst ? footer.label : undefined;
}

/** The phone summary block: the footer's cells in column order, with their headers (the label is the block's title). */
export function footerSummaryItems<N>(
  footer: FooterSpec<N>,
  columns: readonly { key: string; header: string }[],
): { key: string; label: string; value: N }[] {
  const out: { key: string; label: string; value: N }[] = [];
  for (const c of columns) {
    const v = footer.cells[c.key];
    if (v === undefined || v === null || (v as unknown) === false || (v as unknown) === '') continue;
    out.push({ key: c.key, label: c.header, value: v });
  }
  return out;
}
